import { createFileRoute, Link } from '@tanstack/react-router';
import { DocumentUploader } from '../components/DocumentUploader.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { useAuth } from '../hooks/useAuth.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/client/')({
  component: ClientDashboard,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['client']);
  },
});

function ClientDashboard() {
  const { user, logout } = useAuth();
  const profile = trpc.clients.getMine.useQuery();
  const status = profile.data?.status ?? 'draft';
  const readOnly = status === 'approved' || status === 'blocked' || status === 'pending_review';

  return (
    <div className="min-h-screen p-6 max-w-3xl mx-auto space-y-8">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Kunden-Bereich</h1>
          <p className="text-[var(--muted-foreground)]">
            {profile.data?.companyName ?? user?.name ?? user?.email}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => logout()}>
          Abmelden
        </Button>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Firmenprofil</CardTitle>
              <CardDescription className="mt-1">
                Status: <StatusBadge status={status} />
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Link to="/client/profile">
                <Button variant="outline">Profil ansehen</Button>
              </Link>
              {(status === 'draft' || status === 'rejected') && (
                <Link to="/client/onboarding">
                  <Button>Profil bearbeiten</Button>
                </Link>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {status === 'draft' && (
            <p className="text-sm text-[var(--muted-foreground)]">
              Bitte vervollständige dein Firmenprofil.
            </p>
          )}
          {status === 'pending_review' && (
            <p className="text-sm text-[var(--muted-foreground)]">
              Internal Staff prüft dein Profil.
            </p>
          )}
          {status === 'rejected' && (
            <p className="text-sm text-[oklch(0.55_0.2_25)]">
              Profil abgelehnt. Hinweis: {profile.data?.reviewNotes ?? '—'}.
            </p>
          )}
        </CardContent>
      </Card>

      {profile.data && (
        <DocumentUploader ownerType="client" ownerId={profile.data.id} readOnly={readOnly} />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Einsätze</CardTitle>
            <CardDescription>Überlassene Mitarbeiter in Ihrem Betrieb</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/client/assignments">
              <Button variant="outline">Einsätze ansehen</Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Stundenfreigabe</CardTitle>
            <CardDescription>Eingereichte Stunden bestätigen oder ablehnen</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/client/timesheets">
              <Button variant="outline">Stunden freigeben</Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Equal-Treatment</CardTitle>
          <CardDescription>Tarifgruppen, Vergleichslöhne, Zuschläge</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[var(--muted-foreground)]">
            Equal-Treatment-Daten folgen in Phase 2.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
